/**
 * ParticleWaveBackground — GPU particle "wave" for the dashboard backdrop.
 *
 * A dense grid of points laid out on a plane, displaced in Y by layered value
 * noise to read as a slow, organic rolling wave (think a calm ocean seen at a
 * grazing angle). On top of that, a pool of localized RIPPLE PULSES fires on a
 * loose ~2s cadence: each pulse is a radial ring that expands from a random
 * spot, decays in time, and only affects particles within its zone (smooth
 * distance falloff). Because pulses are random in position / radius / strength
 * and overlap, the surface never pulses as a whole — different zones light up
 * at different moments, which keeps it feeling alive rather than mechanical.
 *
 * All the motion lives in the vertex shader (no per-frame CPU vertex work); the
 * CPU only advances time and spawns/retires pulses in a small uniform array.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { emitWavePulse } from './wavePulse';

// Grid resolution (GRID x GRID points). 220 → ~48k points: dense but light.
const GRID = 220;
// Half-extent of the field in world units (the plane spans [-FIELD, FIELD]).
const FIELD = 13;
// Max simultaneous pulses tracked in the shader (a ring buffer on the CPU).
const NUM_PULSES = 16;

// Editor accent colours — each ripple "drop" picks one so the wave flashes in
// the suite's palette: Synth VR (pink), Synth Console (blue), Flow (purple),
// Roobloops (yellow).
const EDITOR_COLORS = ['#ff2e88', '#1cc5f0', '#a22bf0', '#ffd84d'];

// Parse to raw sRGB 0..1 (NOT via THREE.Color, which would linearise them and
// make the drops/ripples render darker than the CSS card swatches). The drop
// shaders output these straight to the buffer, so they match the dashboard.
const EDITOR_RGB: [number, number, number][] = EDITOR_COLORS.map((hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
});

// Falling "drops": coloured points that drift down slowly like distant stars
// and, on landing, trigger the ripple at that spot. One drop maps to a pulse.
const DROP_HEIGHT = 11;        // world Y where a drop spawns (high in the sky)
const DROP_GRAVITY = 2.4;      // base downward acceleration (units/s^2)
const DROP_V0 = 0.15;          // tiny initial speed → starts slow, then accelerates
const DROP_LAND_Y = 0;         // ~the wave's average surface height

// ── Shaders ───────────────────────────────────────────────────────────────────

const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uSize;
  uniform vec4  uPulses[${NUM_PULSES}];        // xy = world pos, z = startTime, w = strength
  uniform float uPulseRadius[${NUM_PULSES}];
  uniform vec3  uPulseColor[${NUM_PULSES}];    // editor accent colour per pulse

  attribute float aRand;

  varying float vH;            // height (for colour)
  varying float vPulse;        // pulse intensity (for glow)
  varying float vFade;         // depth + edge fade (melts into black)
  varying vec3  vPulseColor;   // blended colour of the pulses hitting this point

  // ── cheap 2D value noise + fbm ──────────────────────────────────────────
  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  float fbm(vec2 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      v += amp * vnoise(p);
      p *= 2.0;
      amp *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 pos = position;          // x, 0, z grid
    vec2 xz = pos.xz;
    float t = uTime;

    // Base wave — pronounced rolling dunes/ridges drifting diagonally.
    vec2 flow = vec2(0.07, 0.045);
    float h = 0.0;
    h += (fbm(xz * 0.16 + t * flow) - 0.5) * 3.0;          // big dunes
    h += (fbm(xz * 0.46 + t * flow * 1.8) - 0.5) * 0.9;    // medium detail
    h += sin(xz.x * 0.22 + xz.y * 0.16 + t * 0.4) * 0.28;  // gentle swell

    // Ripple pulses as EXPANDING RINGS: energy (height + colour) lives on a
    // thin wavefront whose radius grows from the centre over time, so each drop
    // reads as a ring growing outward rather than a whole zone flooding.
    float pulseH = 0.0;     // height displacement from the rings
    float pulseMag = 0.0;   // intensity for colour/glow (peaks on the ring)
    vec3 pulseCol = vec3(0.0);
    for (int i = 0; i < ${NUM_PULSES}; i++) {
      vec4 P = uPulses[i];
      float strength = P.w;
      if (strength <= 0.0) continue;
      float age = t - P.z;
      if (age < 0.0) continue;
      float radius = uPulseRadius[i];           // how far the ring travels
      float ringR = age * radius * 0.5;          // reaches the edge at age ~2s
      float d = distance(xz, P.xy);

      float width = max(0.8, radius * 0.12);     // wavefront thickness
      float band = exp(-pow((d - ringR) / width, 2.0));   // Gaussian annulus
      float attack = smoothstep(0.0, 0.16, age);  // appears right at impact, still soft
      // Fade with age, and as the ring nears/passes its outer edge.
      float envelope = exp(-age * 1.1)
                     * (1.0 - smoothstep(0.78, 1.08, ringR / radius));

      float amp = strength * band * attack * envelope;
      pulseH  += amp;                            // a raised ring crest
      pulseMag += amp;
      pulseCol += uPulseColor[i] * amp;
    }
    h += pulseH;

    vH = h;
    vPulse = pulseMag;
    vPulseColor = pulseMag > 0.0001 ? pulseCol / pulseMag : vec3(1.0);

    vec3 displaced = vec3(pos.x, h, pos.z);
    vec4 mv = modelViewMatrix * vec4(displaced, 1.0);
    gl_Position = projectionMatrix * mv;

    // Fade into black: by camera distance (far → horizon) and toward the side
    // edges, so the field has no hard borders.
    float distCam = -mv.z;
    float depthFade = 1.0 - smoothstep(24.0, 46.0, distCam);
    float edgeFade  = 1.0 - smoothstep(${(FIELD * 0.62).toFixed(2)}, ${(FIELD * 1.0).toFixed(2)}, abs(pos.x));
    vFade = clamp(depthFade, 0.0, 1.0) * clamp(edgeFade, 0.0, 1.0);

    // Crisp small points; a touch bigger where a pulse hits. Clamp so points
    // very close to the camera don't blow up.
    float tw = 0.9 + 0.1 * sin(t * 2.0 + aRand * 6.28);
    gl_PointSize = clamp(uSize * tw * (1.0 + vPulse * 1.6) * (260.0 / distCam), 0.0, 6.0);
  }
`;

const FRAG = /* glsl */ `
  precision mediump float;
  uniform vec3 uColorLow;
  uniform vec3 uColorHigh;
  varying float vH;
  varying float vPulse;
  varying float vFade;
  varying vec3  vPulseColor;

  void main() {
    if (vFade <= 0.001) discard;
    // Round point with a small solid core and a thin soft rim (crisp dot).
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float soft = smoothstep(0.5, 0.28, d);

    // Base: silver troughs → white crests. Where a pulse hits, flood toward
    // that editor's accent colour (brightened so the ripple reads as colour).
    float hN = clamp(vH * 0.4 + 0.5, 0.0, 1.0);
    vec3 col = mix(uColorLow, uColorHigh, hN);
    float pTint = clamp(vPulse * 1.6, 0.0, 1.0);
    col = mix(col, vPulseColor * 1.25, pTint);

    float baseAlpha = mix(0.22, 0.85, hN);
    float alpha = soft * vFade * (baseAlpha + clamp(vPulse, 0.0, 1.0) * 0.55);
    gl_FragColor = vec4(col, alpha);
  }
`;

// ── Falling-drop shaders ──────────────────────────────────────────────────────

const DROP_VERT = /* glsl */ `
  uniform float uDropSize;
  uniform float uTime;
  attribute vec3 aColor;
  attribute float aActive;
  attribute float aPhase;     // per-drop random phase for the twinkle
  attribute float aSize;      // per-drop size multiplier
  varying vec3 vColor;
  varying float vActive;
  varying float vTwinkle;
  varying float vLandFade;
  void main() {
    vColor = aColor;
    vActive = aActive;
    // Fade the drop out over its last ~0.9 units so it melts into the surface
    // right as the ripple fires — a continuous hand-off, no gap or pop.
    vLandFade = smoothstep(0.0, 0.9, position.y);
    // Star-like scintillation: two detuned fast oscillators (rates/phases vary
    // per drop) multiplied together, then sharpened — gives a fast, irregular
    // flicker instead of a smooth linear pulse.
    float ph = aPhase * 6.2831;
    float f1 = sin(uTime * (11.0 + aPhase * 9.0) + ph);
    float f2 = sin(uTime * (19.0 + aPhase * 13.0) + ph * 2.7);
    float tw = (0.5 + 0.5 * f1) * (0.5 + 0.5 * f2);   // beats → erratic
    vTwinkle = 0.6 + 0.4 * pow(tw, 1.4);              // gentle flicker (never fades out)
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    // Per-drop size + twinkle so some stars are noticeably bigger than others.
    gl_PointSize = clamp(uDropSize * aSize * (0.7 + 0.5 * vTwinkle) * (320.0 / -mv.z), 1.5, 26.0);
  }
`;

const DROP_FRAG = /* glsl */ `
  precision mediump float;
  varying vec3 vColor;
  varying float vActive;
  varying float vTwinkle;
  varying float vLandFade;
  void main() {
    if (vActive < 0.5) discard;
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    // Solid round dot — flat colour, no radial gradient (tiny edge AA only).
    float edge = smoothstep(0.5, 0.45, d);
    gl_FragColor = vec4(vColor, edge * vTwinkle * vLandFade);
  }
`;

// ── Wave points ────────────────────────────────────────────────────────────────

function WavePoints() {
  const matRef = useRef<THREE.ShaderMaterial>(null);

  // Geometry: a GRID x GRID lattice on the XZ plane, plus a per-point random.
  const geometry = useMemo(() => {
    const count = GRID * GRID;
    const positions = new Float32Array(count * 3);
    const rand = new Float32Array(count);
    let i = 0;
    for (let gx = 0; gx < GRID; gx++) {
      for (let gz = 0; gz < GRID; gz++) {
        const x = (gx / (GRID - 1) - 0.5) * 2 * FIELD;
        const z = (gz / (GRID - 1) - 0.5) * 2 * FIELD;
        positions[i * 3] = x;
        positions[i * 3 + 1] = 0;
        positions[i * 3 + 2] = z;
        rand[i] = Math.random();
        i++;
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.setAttribute('aRand', new THREE.BufferAttribute(rand, 1));
    return g;
  }, []);

  // Uniforms: pulse pool starts empty (strength 0 → skipped in the shader).
  const uniforms = useMemo(() => {
    const pulses: THREE.Vector4[] = [];
    const radii: number[] = [];
    const colors: THREE.Vector3[] = [];
    for (let i = 0; i < NUM_PULSES; i++) {
      pulses.push(new THREE.Vector4(0, 0, -999, 0));
      radii.push(1);
      colors.push(new THREE.Vector3(1, 1, 1));
    }
    return {
      uTime: { value: 0 },
      uSize: { value: 2.2 },
      uPulses: { value: pulses },
      uPulseRadius: { value: radii },
      uPulseColor: { value: colors },
      // Base surface: deep violet troughs → light-lavender crests (purple
      // wave). Ripples still flood editor colour on top.
      uColorLow: { value: new THREE.Color('#5a3aa6') },
      uColorHigh: { value: new THREE.Color('#cbb6ff') },
    };
  }, []);

  // Falling-drop geometry (one slot per pulse) + its material uniforms.
  const drops = useMemo(() => {
    const pos = new Float32Array(NUM_PULSES * 3);
    const col = new Float32Array(NUM_PULSES * 3);
    const act = new Float32Array(NUM_PULSES);
    const phase = new Float32Array(NUM_PULSES);
    const size = new Float32Array(NUM_PULSES);
    for (let i = 0; i < NUM_PULSES; i++) { pos[i * 3 + 1] = -999; phase[i] = Math.random(); size[i] = 1; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
    g.setAttribute('aActive', new THREE.BufferAttribute(act, 1));
    g.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1));
    g.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    return { g, pos, col, act, phase, size };
  }, []);
  const dropUniforms = useMemo(() => ({ uDropSize: { value: 1.6 }, uTime: { value: 0 } }), []);

  // Per-slot live drop state (null = no drop falling in that slot).
  const dropState = useRef<Array<{ x: number; z: number; y: number; vy: number; grav: number; r: number; g: number; b: number } | null>>(
    new Array(NUM_PULSES).fill(null),
  );

  // Scheduler.
  const nextSpawn = useRef(0.4);
  const cursor = useRef(0);

  // Spawn one drop into slot `idx`, starting at height `startY`. Caller flags
  // the buffer attributes dirty.
  const spawnDrop = (idx: number, startY: number) => {
    // Keep drops inside the wave grid (and away from the faded edges) so every
    // landing actually lands on the surface and produces a visible ripple.
    // Z spans the near rows (positive → upper band on screen after the flip)
    // through the far rows (negative → lower band) so collisions are spread
    // across the whole visible wave, not just one band.
    const x = (Math.random() - 0.5) * 2 * FIELD * 0.8;
    const z = (0.55 - Math.random() * 1.4) * FIELD;    // ~[+0.55F .. -0.85F], on-grid
    const [cr, cg, cb] = EDITOR_RGB[(Math.random() * EDITOR_RGB.length) | 0];
    // Per-drop gravity so some accelerate harder than others. A drop seeded
    // mid-fall gets the speed it would already have (vy² = v0² + 2·g·fallen),
    // so motion looks continuous from the very first frame.
    const grav = DROP_GRAVITY * (0.55 + Math.random() * 1.5);
    const fallen = Math.max(0, DROP_HEIGHT - startY);
    const vy = Math.sqrt(DROP_V0 * DROP_V0 + 2 * grav * fallen);
    dropState.current[idx] = { x, z, y: startY, vy, grav, r: cr, g: cg, b: cb };
    drops.pos[idx * 3] = x;
    drops.pos[idx * 3 + 1] = startY;
    drops.pos[idx * 3 + 2] = z;
    drops.col[idx * 3] = cr;
    drops.col[idx * 3 + 1] = cg;
    drops.col[idx * 3 + 2] = cb;
    drops.phase[idx] = Math.random();
    drops.size[idx] = 0.4 + Math.random() * Math.random() * 1.3; // mostly small
    drops.act[idx] = 1;
  };

  // Pre-seed the sky so stars are already falling the moment the page loads
  // (each at a random height along its descent).
  useEffect(() => {
    const initial = Math.min(12, NUM_PULSES);
    for (let k = 0; k < initial; k++) spawnDrop(k, Math.random() * DROP_HEIGHT);
    cursor.current = initial;
    for (const a of ['position', 'aColor', 'aActive', 'aPhase', 'aSize'] as const) {
      drops.g.attributes[a].needsUpdate = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    if (matRef.current) matRef.current.uniforms.uTime.value = t;
    dropUniforms.uTime.value = t;

    const pulses = uniforms.uPulses.value as THREE.Vector4[];
    const radii = uniforms.uPulseRadius.value as number[];
    const colors = uniforms.uPulseColor.value as THREE.Vector3[];

    // ── Spawn new falling drops (a few twinkling at a time, slow fall) ───────
    if (t >= nextSpawn.current) {
      const burst = Math.random() < 0.5 ? 2 : 1;
      for (let b = 0; b < burst; b++) {
        spawnDrop(cursor.current % NUM_PULSES, DROP_HEIGHT);
        cursor.current++;
      }
      drops.g.attributes.aColor.needsUpdate = true;
      drops.g.attributes.aPhase.needsUpdate = true;
      drops.g.attributes.aSize.needsUpdate = true;
      nextSpawn.current = t + 0.7 + Math.random() * 0.7;  // frequent → a sky of stars
    }

    // ── Drift drops down slowly; on landing, trigger the ripple at that spot ─
    let posDirty = false, actDirty = false;
    for (let i = 0; i < NUM_PULSES; i++) {
      const d = dropState.current[i];
      if (!d) continue;
      d.vy = Math.min(d.vy + d.grav * dt, 16);  // accelerate (start slow → speed up)
      d.y -= d.vy * dt;
      drops.pos[i * 3 + 1] = d.y;
      posDirty = true;
      if (d.y <= DROP_LAND_Y + 0.55) {
        // Fire the ripple just BEFORE the drop fully reaches the surface so the
        // ring appears in sync with the touch-down (not half a second later).
        // Only on the wave; stars in the margins just fade out.
        if (Math.abs(d.x) <= FIELD && Math.abs(d.z) <= FIELD) {
          const strength = 0.35 + Math.random() * 0.5;
          radii[i] = FIELD * (0.42 + Math.random() * 0.51);  // +50% reach
          pulses[i].set(d.x, d.z, t, strength);
          colors[i].set(d.r, d.g, d.b);
          emitWavePulse();   // light up the SYNAPSE outline on impact
        }
        drops.act[i] = 0;
        drops.pos[i * 3 + 1] = -999;
        dropState.current[i] = null;
        actDirty = true;
      }
    }
    if (posDirty) drops.g.attributes.position.needsUpdate = true;
    if (actDirty) drops.g.attributes.aActive.needsUpdate = true;
    // Colours only change at spawn; flag once per frame is cheap enough.
    drops.g.attributes.aColor.needsUpdate = true;
  });

  return (
    <>
      <points geometry={geometry} frustumCulled={false}>
        <shaderMaterial
          ref={matRef}
          vertexShader={VERT}
          fragmentShader={FRAG}
          uniforms={uniforms}
          transparent
          depthWrite={false}
          depthTest={false}
          blending={THREE.NormalBlending}
        />
      </points>
      <points geometry={drops.g} frustumCulled={false}>
        <shaderMaterial
          vertexShader={DROP_VERT}
          fragmentShader={DROP_FRAG}
          uniforms={dropUniforms}
          transparent
          depthWrite={false}
          depthTest={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </>
  );
}

// ── Public component ─────────────────────────────────────────────────────────

export default function ParticleWaveBackground() {
  return (
    <div className="dash-wave-fade" style={wrapStyle} aria-hidden>
      <Canvas
        // Low, near-ground camera + wide FOV → strong grazing perspective so
        // the wave recedes to a horizon (like the reference) instead of reading
        // top-down. Look forward/slightly-down toward the far edge.
        camera={{ position: [0, 4.0, 12], fov: 58 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
        style={{ width: '100%', height: '100%' }}
        onCreated={({ camera }) => camera.lookAt(0, 0.0, -8)}
      >
        <WavePoints />
      </Canvas>
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',   // never intercept clicks meant for the dashboard
  // Experiment: flip the whole scene vertically — the wave sits at the TOP and
  // the drops appear to rise from the bottom up into it (their world physics
  // are unchanged; only the rendered canvas is mirrored).
  transform: 'scaleY(-1)',
};
